import { Component, ViewChild, ElementRef, Input, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-audio-player-v2',
  templateUrl: './audio-player-v2.component.html',
  styleUrl: './audio-player-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudioPlayerComponentV2 implements AfterViewInit {
  isPlay: boolean = true;
  isVolumeMuted: boolean = false;
  currentVolume: number = 1;
  volumeIcon: string = 'volume_up';
  progress: number = 0;
  isSeeking: boolean = false;

  @Input() src!: string;
  @Input() title: string = 'Untitled';
  @Input() autoplay?: boolean = false;

  @ViewChild('audio', { static: true }) audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('volume', { static: true }) volume!: ElementRef<any>;
  @ViewChild('currentTime', { static: true }) currentTimeElRef!: ElementRef<HTMLElement>;
  @ViewChild('duration', { static: true }) durationElRef!: ElementRef<HTMLElement>;
  @ViewChild('progressBar', { static: true }) progressBarRef!: ElementRef<HTMLElement>;

  constructor() {
    this.isPlay = !!this.autoplay;
  }

  ngAfterViewInit() {
    const audioElem = this.audioRef.nativeElement;
    const currentTimeElem = this.currentTimeElRef.nativeElement;
    const durationElem = this.durationElRef.nativeElement;
    const progressBarElem = this.progressBarRef.nativeElement;

    audioElem.addEventListener('loadedmetadata', () => {
      durationElem.textContent = this.formatTime(audioElem.duration);
    });

    audioElem.addEventListener('timeupdate', () => {
      if (!this.isSeeking) {
        currentTimeElem.textContent = this.formatTime(audioElem.currentTime);
        audioElem.addEventListener('timeupdate', this.syncProgress.bind(this));
        const progressPercent = (audioElem.currentTime / audioElem.duration) * 100;
        if (progressPercent === 100) this.isPlay = false;
        progressBarElem.style.width = `${progressPercent}%`;
      }
    });
  }

  togglePlay(): void {
    const audio = this.audioRef.nativeElement;

    if (audio.paused) {
      audio.play();
      this.isPlay = true;
    } else {
      audio.pause();
      this.isPlay = false;
    }
  }

  updateVolumeIcon(): void {
    if (this.isVolumeMuted) {
      this.volumeIcon = 'volume_mute';
      return;
    }

    const volume: number = this.audioRef?.nativeElement?.volume;

    if (volume > 0 && volume < 0.25) {
      this.volumeIcon = 'volume_mute';
    } else if (volume >= 0.25 && volume < 0.75) {
      this.volumeIcon = 'volume_down';
    } else {
      this.volumeIcon = 'volume_up';
    }
  }

  public onVolumeChange(event: Event): void {
    this.currentVolume = (event.target as HTMLInputElement).valueAsNumber;
    this.isVolumeMuted = this.currentVolume === 0;

    this.audioRef.nativeElement.volume = this.currentVolume;
    this.updateVolumeIcon();
  }

  public onValueMute(): void {
    this.isVolumeMuted = !this.isVolumeMuted;
    console.log('muting', this.isVolumeMuted);

    if (this.isVolumeMuted) {
      this.audioRef.nativeElement.volume = 0;
      this.volume.nativeElement.value = 0;
    } else {
      if (this.currentVolume === 0) {
        this.audioRef.nativeElement.volume = 1;
        this.volume.nativeElement.value = 1;
      } else {
        this.audioRef.nativeElement.volume = this.currentVolume;
        this.volume.nativeElement.value = this.currentVolume;
      }
    }

    this.updateVolumeIcon();
  }

  public updateProgress(event: Event): void {
    this.isSeeking = true;

    const audio = this.audioRef.nativeElement;
    const value = (event.target as HTMLInputElement).value;
    const currentTimeElem = this.currentTimeElRef.nativeElement;

    this.progress = Number(value);
    currentTimeElem.textContent = this.formatTime((audio.duration / 100) * Number(value));
  }

  public setProgress(event: Event): void {
    const audio = this.audioRef.nativeElement;
    const value = (event.target as HTMLInputElement).value;
    audio.currentTime = (audio.duration / 100) * Number(value);

    this.isSeeking = false;
  }

  private formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    seconds = Math.floor(seconds % 60);

    return `${hours ? `${hours}:` : ''}${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  public syncProgress(): void {
    if (!this.isSeeking) {
      const audio = this.audioRef.nativeElement;
      this.progress = (audio.currentTime / audio.duration) * 100;
    }
  }

  public onRewind(isForward: boolean): void {
    const audio = this.audioRef.nativeElement;
    audio.currentTime = isForward ? audio.currentTime + 5 : audio.currentTime - 5;
  }
}
